import { reorder } from "@atlaskit/pragmatic-drag-and-drop/reorder";
import { PlusIcon, SaveIcon } from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router";
import type { BoardResponse } from "shared/model/board/board.response";
import type { BoardColumnDto } from "shared/model/board/editBoard.dto";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import { BoardService } from "@/api/Board.service";
import { BoardCreator } from "@/components/molecules/board_creator/BoardCreator";
import { BoardCreatorColumn } from "@/components/molecules/board_creator/BoardCreatorColumn";
import {
  PageCard,
  PageCardContent,
  PageCardHeader,
} from "@/components/molecules/page_card/PageCard";
import Navbar from "@/components/organisms/navbar/Navbar";
import { ResponsivePageLayout } from "@/components/organisms/responsive_page_layout/ResponsivePageLayout";
import { Button } from "@/components/ui/button";

const MAX_COLUMNS = 6;

export const TeamBoardEditView: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const [board, setBoard] = useState<BoardResponse>();

  useEffect(() => {
    if (!teamId) return;

    BoardService.getBoard(teamId).then((board) => setBoard(board));
  }, [teamId]);

  if (!teamId) {
    return <Navigate to={"/"} />;
  }

  if (!board) {
    return (
      <div>
        <Navbar />
        loading
      </div>
    );
  }

  const onAddColumn = () => {
    const column: BoardColumnDto = {
      id: uuidv4(),
      name: "",
      order: board.columns.length,
    };

    setBoard({ ...board, columns: [...board.columns, column] });
  };

  const onChangeColumn = (id: string, column: BoardColumnDto) => {
    const columnIndex = board.columns.findIndex((col) => col.id === id);

    const newColumns = [...board.columns];
    newColumns[columnIndex] = column;

    setBoard({ ...board, columns: newColumns });
  };

  const onDeleteColumn = (id: string) => {
    if (board.defaultColumnId === id) {
      return;
    }

    const columnIndex = board.columns.findIndex((col) => col.id === id);
    const newColumns = [...board.columns];

    newColumns.splice(columnIndex, 1);

    setBoard({ ...board, columns: newColumns });
  };

  const saveBoard = () => {
    BoardService.editBoard(teamId, board)
      .then(() => {
        navigate(`/team/${teamId}/board`);
      })
      .then(() => {
        toast.success("Tablicę zapisano");
      })
      .catch((e) => {
        console.log(e);
        toast.error("Wystąpił błąd");
      });
  };

  return (
    <>
      <Navbar />

      <ResponsivePageLayout>
        <PageCard>
          <PageCardHeader>Edycja tablicy</PageCardHeader>

          <PageCardContent>
            <div className={"flex justify-end"}>
              <Button
                disabled={board.columns.length >= MAX_COLUMNS}
                onClick={onAddColumn}
              >
                <PlusIcon />
                Nowa Kolumna
              </Button>
            </div>

            <BoardCreator
              onColumnReorder={({ fromId, toId }) => {
                const columns = board?.columns ?? [];
                const fromIndex = columns.findIndex((c) => c.id === fromId);
                const toIndex = columns.findIndex((c) => c.id === toId);
                if (fromIndex === -1 || toIndex === -1) return;
                if (fromIndex === toIndex) return;

                const reorderedColumns = reorder({
                  list: columns,
                  startIndex: fromIndex,
                  finishIndex: toIndex,
                }).map((column, i) => ({
                  ...column,
                  order: i,
                }));

                setBoard({
                  ...board,
                  columns: reorderedColumns,
                });
              }}
            >
              {board.columns
                .sort((a, b) => a.order - b.order)
                .map((col, index) => (
                  <BoardCreatorColumn
                    id={col.id}
                    key={col.id}
                    name={col.name}
                    desc={""}
                    className={index === 0 ? "ring-primary ring-2" : undefined}
                    onChange={({ name }) =>
                      onChangeColumn(col.id, {
                        id: col.id,
                        name: name,
                        order: col.order,
                      })
                    }
                    onDelete={() => onDeleteColumn(col.id)}
                  />
                ))}
            </BoardCreator>

            <Button className={"mt-4"} onClick={saveBoard}>
              <SaveIcon />
              Zapisz tablicę
            </Button>
          </PageCardContent>
        </PageCard>
      </ResponsivePageLayout>
    </>
  );
};
